import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { styled } from '@mui/material/styles';
import 'firebase/compat/auth';
import SectionHead from '@components/UI/Section/components/SectionHead';
import SectionHeading from '@components/UI/Section/components/SectionHeading';
import SectionSubheading from '@components/UI/Section/components/SectionSubheading';
import ErrorMessage from '@components/UI/ErrorMessage';
import SectionContainer from '@components/UI/Section/components/SectionContainer';
import CoreButton from '@components/Buttons/CoreButton';
import Flex from '@components/Layout/Flex';
import TextInput, { StyledLabel } from '@components/Fields/TextInput';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/solid';
import useCountdown from '@components/Hooks/useCountdown';
import useResendConfirmEmail from '@hooks/API/useResendConfirmEmail';
import { useEffect, useState } from 'react';
import { Redirect } from 'react-router';
import useTrackUserFlow from '@hooks/useTrackUserFlow';
import { useUserState } from '@context/UserContext';
import BasicHeader from '@domain/Account/Registration/BasicHeader';
import useTrackEvent from '@hooks/useTrackEvent';
import RegistrationFooter from './RegistrationFooter';
import useChangeSignupEmail from '../hooks/useChangeSignupEmail';
const StyledOuterContainer = styled('div') `
  padding: 10px;
`;
const StyledContainer = styled(SectionContainer) `
  box-shadow: none;
  max-width: 430px;
  display: flex;
  flex-direction: column;
  font-family: ${(props) => props.theme.typography.fontFamily};
  font-size: ${(props) => props.theme.typography.fontSize};
  font-weight: 600;
  margin-left: auto;
  margin-right: auto;
  margin-top: 10vh;
  border: 1px solid ${(props) => props.theme.palette.grey[100]};
`;
const PanelContainer = styled('div') `
  background: #fff;
  padding: ${(props) => props.theme.spacing(2, 3)};
  border-top: 1px solid ${(props) => props.theme.palette.grey[100]};
  border-bottom: 1px solid ${(props) => props.theme.palette.grey[100]};
  &:last-of-type {
    border-bottom: 0;
  }
`;
const SubText = styled('span') `
  font-weight: 500;
  color: ${(props) => props.theme.palette.grey[500]};
`;
const PanelContent = styled('p') `
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
  color: ${(props) => props.theme.palette.grey[400]};
`;
const StyledButton = styled(CoreButton) `
  width: 100%;
  > svg {
    width: 24px;
    height: 24px;
    margin-right: 4px;
  }
`;
const EditButton = styled(StyledButton) `
  margin: ${({ theme }) => theme.spacing(2, 0)};
  padding: 10.5px 16px !important;
  height: 39px;
`;
const EmailLockedContainer = styled('div') `
  position: relative;
`;
const LockIcon = styled(LockClosedIcon) `
  position: absolute;
  right: 10px;
  bottom: 24px;
  width: 24px;
  height: 24px;
`;
const StyledInput = styled(TextInput) `
  &:disabled {
    color: #25282b !important;
    -webkit-text-fill-color: #25282b !important;
  }
`;
const StyledErrorMessage = styled(ErrorMessage) `
  padding-top: 1em;
`;
const useResendEmail = () => {
    const { resendConfirmEmail, errorMessage } = useResendConfirmEmail();
    const trackEvent = useTrackEvent();
    const sendingTimeoutSeconds = 10;
    const retryTimeoutSeconds = 60;
    const { setRemainingSeconds, remainingSeconds } = useCountdown({
        startSeconds: 0,
    });
    const resendEmailText = 'Resend verification email';
    const resendEmail = () => {
        resendConfirmEmail();
        setRemainingSeconds(sendingTimeoutSeconds + retryTimeoutSeconds);
        trackEvent({
            type: 'account_registration',
            data: {
                eventName: 'account registration',
                label: resendEmailText,
                moduleName: 'Button',
                position: 2,
            },
        });
    };
    if (remainingSeconds <= 0) {
        return { disabled: false, text: resendEmailText, resendEmail, errorMessage };
    }
    if (remainingSeconds <= retryTimeoutSeconds) {
        return { disabled: true, text: `Email sent! Retry in ${remainingSeconds} seconds`, errorMessage };
    }
    return { disabled: true, text: 'Sending...', errorMessage };
};
const CompleteEmailVerificationPage = ({ updateUser, email, handle }) => {
    var _a, _b;
    const emailLabelId = 'email';
    const { userDetails, isLoading: isLoadingUser } = useUserState();
    const trackEvent = useTrackEvent();
    const [editEmail, setEditEmail] = useState(email);
    const [showEditEmailInput, setShowEditEmailInput] = useState(false);
    const trackUserFlow = useTrackUserFlow();
    const { text, resendEmail, disabled, errorMessage: resendEmailErrorMessage } = useResendEmail();
    const { isLoading, isError, clearError, changeEmail, errorMessage } = useChangeSignupEmail({
        onSuccess: () => {
            setShowEditEmailInput(false);
            updateUser({ email: editEmail });
        },
        originalEmail: email,
    });
    // Track verify email address
    useEffect(() => {
        var _a;
        if (!((_a = userDetails === null || userDetails === void 0 ? void 0 : userDetails.verification) === null || _a === void 0 ? void 0 : _a.hasVerifiedEmail)) {
            trackUserFlow('account_register_step', { formStep: 'Verify email address' });
        }
    }, [trackUserFlow, (_a = userDetails === null || userDetails === void 0 ? void 0 : userDetails.verification) === null || _a === void 0 ? void 0 : _a.hasVerifiedEmail]);
    const displayEmail = editEmail || email;
    const onEditEmailChange = (e) => {
        clearError();
        setEditEmail(e.target.value);
        trackEvent({
            type: 'account_registration',
            data: {
                eventName: 'account registration',
                label: 'Edit',
                moduleName: 'Button',
                position: 1,
            },
        });
    };
    const saveEmail = () => {
        changeEmail(editEmail);
    };
    if ((_b = userDetails === null || userDetails === void 0 ? void 0 : userDetails.verification) === null || _b === void 0 ? void 0 : _b.hasVerifiedEmail) {
        return _jsx(Redirect, { to: "/compete" });
    }
    return (_jsxs("div", { children: [_jsx(BasicHeader, { isLoadingUser: isLoadingUser }), _jsxs(StyledOuterContainer, { "data-testid": "complete-email-verification-container", children: [_jsxs(StyledContainer, { children: [_jsx(SectionHead, { bottomBorder: true, children: _jsxs("div", { children: [_jsx(SectionHeading, { children: "Please verify your email address" }), _jsx(SectionSubheading, { children: "You should have received an email when signing up, please check your inbox or spam folder." })] }) }), _jsxs(PanelContainer, { children: [_jsx(PanelContent, { children: "This is your current username and email address. You may use the form below to update your preferred email address." }), _jsxs(EmailLockedContainer, { children: [_jsx(StyledInput, { id: "username", disabled: true, label: "Username", value: handle }), _jsx(LockIcon, {})] }), _jsxs(StyledLabel, { htmlFor: emailLabelId, children: ["Email Address ", _jsx(SubText, { children: "(You must verify your email address)" })] }), _jsx(Flex, { children: showEditEmailInput ? (_jsxs(_Fragment, { children: [_jsx(StyledInput, { error: isError, id: emailLabelId, onChange: onEditEmailChange, value: editEmail, placeholder: "Update Your Email Address...", type: "email" }), _jsx("div", { children: _jsxs(EditButton, { disabled: isLoading, onClick: saveEmail, children: [_jsx(CheckCircleIcon, {}), isLoading ? 'Saving...' : 'Save'] }) })] })) : (_jsxs(_Fragment, { children: [_jsx(StyledInput, { disabled: true, id: emailLabelId, value: displayEmail, placeholder: "Your Email Address...", type: "email" }), _jsx("div", { children: _jsxs(EditButton, { onClick: () => setShowEditEmailInput(true), children: [_jsx(PencilSquareIcon, {}), "Edit"] }) })] })) }), errorMessage && (_jsx(ErrorMessage, { textAlign: "center", error: true, children: errorMessage }))] }), _jsxs(PanelContainer, { children: [_jsx(PanelContent, { children: "Click below to resend your verification email" }), _jsx(Flex, { children: _jsxs(StyledButton, { onClick: resendEmail, disabled: disabled || isError || isLoading || showEditEmailInput, children: [_jsx(EnvelopeIcon, {}), text] }) }), resendEmailErrorMessage && (_jsx(StyledErrorMessage, { textAlign: "center", error: true, children: resendEmailErrorMessage }))] })] }), _jsx(RegistrationFooter, {})] })] }));
};
export default CompleteEmailVerificationPage;
